import React, { useEffect, useState, useRef } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createPlanningHorizon,
  updatePlanningHorizon,
  deletePlanningHorizon,
  getPlanningHorizon
} from '../api/planning-horizonAPI';
import { fetchData } from '../utils/apiHelpers';
import { Icon } from '@iconify/react';
import $ from 'jquery';

const PlanningLeadTimeListLayer = () => {
  const userProfile = useUserProfile();
  const [leadTimes, setLeadTimes] = useState([]);
  const [newItem, setNewItem] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchLeadTimes = () => {
    fetchData(getPlanningHorizon, {}, setLeadTimes);
  };


  useEffect(() => {
    fetchLeadTimes();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (leadTimes.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
        order: [[2, 'asc']],
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [leadTimes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const validateForm = () => {
    const newErrors = {};
    if (!newItem.name?.trim()) newErrors.name = 'Name is required';
    if (!newItem.valueInDays || isNaN(newItem.valueInDays)) newErrors.valueInDays = 'Valid number of days is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewItem(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      await deletePlanningHorizon(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchLeadTimes();
    } catch (error) {
      console.error('Error deleting lead time:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        name: newItem.name,
        valueInDays: Number(newItem.valueInDays),
        sortOrder: newItem.sortOrder ? Number(newItem.sortOrder) : 1,
      };
      if (editingId) {
        await updatePlanningHorizon(editingId, payload);
      } else {
        await createPlanningHorizon(payload);
      }
      setShowModal(false);
      setEditingId(null);
      setNewItem({});
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchLeadTimes();
    } catch (error) {
      console.error('Error saving lead time:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('planning-horizon:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewItem({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Lead Time
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Days</th>
                <th>Sort Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leadTimes.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td>{item.valueInDays}</td>
                  <td>{item.sortOrder}</td> 
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('planning-horizon:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('planning-horizon:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Lead Time' : 'New Lead Time'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name *</label>
                    <input
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newItem.name || ''}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Days *</label>
                    <input
                      type="number"
                      className={`form-control ${errors.valueInDays ? 'is-invalid' : ''}`}
                      name="valueInDays"
                      value={newItem.valueInDays || ''}
                      onChange={handleInputChange}
                    />
                    {errors.valueInDays && <div className="invalid-feedback">{errors.valueInDays}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      className={`form-control`}
                      name="sortOrder"
                      value={newItem.sortOrder || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningLeadTimeListLayer;
